/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useMemo, useState} from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useUsid, useEncUserId, useCustomerType, useDNT} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {keysToCamel} from '@salesforce/retail-react-app/app/utils/utils'
import logger from '@salesforce/retail-react-app/app/utils/logger-instance'

/**
 * Every EinsteinAPI method takes its optional extras as the last declared parameter (`args`), at
 * position `argsIndex`. The user identifiers must be merged INTO that object.
 *
 * The upstream wrappers appended them as a new positional argument (`args.concat(userParameters)`).
 * When the caller already passes `args` (e.g. viewSearch / viewCategory with sortingRule and
 * itemRange) the identifiers land in a slot the method never reads, so the activity is sent without
 * `cookieId` and Einstein drops it, even though the API still answers 200.
 *
 * @param {Array} callArgs - Arguments received by the wrapper.
 * @param {number} argsIndex - Position of the `args` parameter in the EinsteinAPI method.
 * @param {{cookieId?: string, userId?: string}} userParameters - Shopper identifiers.
 * @returns {Array} Arguments to pass to the EinsteinAPI method.
 */
export const withUserParameters = (callArgs, argsIndex, userParameters) => {
    const positional = Array.from({length: argsIndex}, (_, index) => callArgs[index])
    return [...positional, {...(callArgs[argsIndex] || {}), ...userParameters}]
}

/**
 * Sites that keep the configured (environment) Einstein site ID instead of a per-site one.
 * bc-cn: how Einstein is set up for China is still unknown, so it keeps the pre-fix behavior.
 */
export const EINSTEIN_CONFIGURED_SITE_ID_SITES = ['bc-cn']

/**
 * Einstein site IDs are per storefront site, `<realm>-<siteId>` (e.g. `abcp-bc-us`), the same value
 * SFRA sends through `CQuotient.clientId`. The configured `siteId` is one per environment (EU), so
 * the realm is taken from it and the site part from the current storefront site.
 *
 * @param {string} configuredSiteId - `einsteinAPI.siteId` from config (e.g. `abcp-bc-eu`).
 * @param {string} [storefrontSiteId] - Current site (e.g. `bc-us`).
 * @returns {string} Einstein site ID to use.
 */
export const resolveEinsteinSiteId = (configuredSiteId, storefrontSiteId) => {
    const realm = configuredSiteId?.split('-')[0]
    if (!realm || !storefrontSiteId || EINSTEIN_CONFIGURED_SITE_ID_SITES.includes(storefrontSiteId)) return configuredSiteId
    return `${realm}-${storefrontSiteId}`
}

export class EinsteinAPI {
    constructor({host, einsteinId, siteId, isProduction, dnt}) {
        this.dnt = dnt
        this.siteId = siteId
        this.isProduction = isProduction
        this.host = host
        this.einsteinId = einsteinId
    }

    /**
     * Given a POJO append the correct site and environment values
     *
     * @param {object} params
     * @returns {object} The decorated body object.
     */
    _buildBody(params) {
        const instanceType_prd = 'prd'
        const instanceType_sbx = 'sbx'

        const body = {...params}

        // The first part of the siteId is the realm
        if (this.siteId) {
            body.realm = this.siteId.split('-')[0]
        }

        if (this.isProduction) {
            body.instanceType = instanceType_prd
        } else {
            body.instanceType = instanceType_sbx
        }

        return body
    }

    /**
     * Given a product or item source, returns the product data that Einstein requires
     */
    _constructEinsteinProduct(product) {
        if (product.type && (product.type.master || product.type.variant)) {
            // handle variants for PDP / viewProduct
            // Assumes product is a Product object from SCAPI Shopper-Products:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=type%3AProduct
            return {
                id: product.master.masterId,
                sku: product.id,
                altId: '',
                altIdType: '',
            }
        } else if (
            product.productType &&
            (product.productType.master ||
                product.productType.variant ||
                product.productType.set ||
                product.productType.bundle ||
                product.productType.variationGroup ||
                product.productType.item)
        ) {
            // handle variants & sets for PLP / viewCategory & viewSearch
            // Assumes product is a ProductSearchHit from SCAPI Shopper-Search:
            // https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=type%3AProductSearchHit
            return {
                id: product.productId,
                sku: product.productId, //TODO: Should we switch this to product.representedProduct.id once we allow non-master products in search results?
                altId: '',
                altIdType: '',
            }
        } else {
            // handles non-variants
            return {
                id: product.id,
                sku: '',
                altId: '',
                altIdType: '',
            }
        }
    }

    /**
     * Given a cart item, returns the data that Einstein requires
     *
     * Assumes item is a ProductItemfrom SCAPI Shopper-Baskets:
     * https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-baskets?meta=type%3AProductItem
     */
    _constructEinsteinItem(item) {
        return {
            id: item.productId,
            sku: '',
            price: item.price,
            quantity: item.quantity,
        }
    }

    /**
     * BC POLICY — Do-Not-Track (DNT) and Einstein.
     *
     * Decision: DNT must be honored for ANALYTICS/ACTIVITY calls (viewProduct, viewReco,
     * addToCart, checkout steps, ...), but Einstein must still RETURN recommendations so the
     * PDP/PLP carousels are never left empty. Without consent the recommender simply serves
     * generic (non-personalized) results instead of personalized ones.
     *
     * Why the DNT guard is NOT here: `einsteinFetch` is shared by both activities and
     * recommendation retrieval. The upstream base template gated it globally
     * (`if (this.dnt !== false) return {}`), which also suppressed getRecommendations and left the
     * carousels empty when consent was missing. We moved that guard to `einsteinActivityFetch`
     * so only activities are suppressed under DNT.
     *
     * On passing the tracking state to Einstein: the Einstein Recommendations API has NO request
     * parameter/header to declare the consent/DNT state for a recs call. In B2C Commerce the DNT
     * preference is carried by the `dw_dnt` cookie and the DNT claim in the SLAS access token
     * (see "Manage Shopper Tracking Preferences" and PWA Kit "Tracking Consent Banner" docs); it
     * governs activity tracking, not whether recommendations are returned. So the correct lever is
     * client-side: gate the *activities* on DNT (done below) and always call getRecommendations.
     * If personalization must be fully disabled without consent, that is enforced backend-side via
     * the SLAS DNT claim / Business Manager privacy preferences, not by withholding the recs call.
     */
    async einsteinFetch(endpoint, method, body) {
        const headers = {
            'Content-Type': 'application/json',
            'x-cq-client-id': this.einsteinId,
        }

        if (body) {
            body = this._buildBody(body)
        }

        let response
        try {
            response = await fetch(`${this.host}/v3${endpoint}`, {
                method: method,
                headers: headers,
                ...(body && {
                    body: JSON.stringify(body),
                }),
            })
        } catch (error) {
            logger.error('Einstein request failed', {
                namespace: 'useEinstein.einsteinFetch',
                additionalProperties: {error: error},
            })
        }

        if (!response?.ok) {
            return {}
        }

        try {
            const responseJson = await response.json()

            return keysToCamel(responseJson)
        } catch (error) {
            logger.error('Error parsing response JSON', {
                namespace: 'useEinstein.einsteinFetch',
                additionalProperties: {error: error},
            })
            return {}
        }
    }

    /**
     * Fetch wrapper for tracking ACTIVITIES only (viewProduct, viewReco, addToCart, ...).
     * These honor DNT: skipped unless tracking consent is explicitly given (dnt === false).
     * Recommendation RETRIEVAL (getRecommenders/getRecommendations/getZoneRecommendations) does
     * NOT use this wrapper on purpose — see the BC POLICY note on `einsteinFetch` above.
     */
    async einsteinActivityFetch(endpoint, method, body) {
        if (this.dnt !== false) return {}

        return this.einsteinFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user views a product.
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async sendViewProduct(product, args) {
        const endpoint = `/activities/${this.siteId}/viewProduct`
        const method = 'POST'
        const body = {
            product: this._constructEinsteinProduct(product),
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user views search results.
     **/
    async sendViewSearch(searchText, searchResults, args) {
        const endpoint = `/activities/${this.siteId}/viewSearch`
        const method = 'POST'

        const products = searchResults?.hits?.map((product) => this._constructEinsteinProduct(product)) ?? []

        const body = {
            searchText,
            products,
            showProducts: Boolean(products.length), // Needed by Reports and Dashboards to differentiate searches with results vs no results
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user clicks on a search result.
     **/
    async sendClickSearch(searchText, product, args) {
        const endpoint = `/activities/${this.siteId}/clickSearch`
        const method = 'POST'
        const body = {
            searchText,
            product: this._constructEinsteinProduct(product),
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user views a category.
     **/
    async sendViewCategory(category, searchResults, args) {
        const endpoint = `/activities/${this.siteId}/viewCategory`
        const method = 'POST'

        const products = searchResults?.hits?.map((product) => this._constructEinsteinProduct(product))

        const body = {
            category: {
                id: category.id,
            },
            products,
            showProducts: true, // Needed by Reports and Dashboards to differentiate searches with results vs no results
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user clicks a product from the category page.
     * Not meant to be used when the user clicks a category from the nav bar.
     **/
    async sendClickCategory(category, product, args) {
        const endpoint = `/activities/${this.siteId}/clickCategory`
        const method = 'POST'
        const body = {
            category: {
                id: category.id,
            },
            product: this._constructEinsteinProduct(product),
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user views a set of recommendations
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async sendViewReco(recommenderDetails, products, args) {
        const endpoint = `/activities/${this.siteId}/viewReco`
        const method = 'POST'
        const {__recoUUID, recommenderName} = recommenderDetails
        const body = {
            recommenderName,
            __recoUUID,
            products: products,
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user clicks on a recommendation
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async sendClickReco(recommenderDetails, product, args) {
        const endpoint = `/activities/${this.siteId}/clickReco`
        const method = 'POST'
        const {__recoUUID, recommenderName} = recommenderDetails
        const body = {
            recommenderName,
            __recoUUID,
            product: this._constructEinsteinProduct(product),
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user views a page.
     * Use this only for pages where another activity does not fit. (ie. on the PDP, use viewProduct rather than this)
     **/
    async sendViewPage(path, args) {
        const endpoint = `/activities/${this.siteId}/viewPage`
        const method = 'POST'
        const body = {
            currentLocation: path,
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user starts the checkout process.
     **/
    async sendBeginCheckout(basket, args) {
        const endpoint = `/activities/${this.siteId}/beginCheckout`
        const method = 'POST'
        const products = basket.productItems.map((item) => this._constructEinsteinItem(item))
        const subTotal = basket.productSubTotal
        const body = {
            products: products,
            amount: subTotal,
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user reaches the given step during checkout.
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async sendCheckoutStep(stepName, stepNumber, basket, args) {
        const endpoint = `/activities/${this.siteId}/checkoutStep`
        const method = 'POST'
        const body = {
            stepName,
            stepNumber,
            basketId: basket.basketId,
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Tells the Einstein engine when a user adds an item to their cart.
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async sendAddToCart(items, args) {
        const endpoint = `/activities/${this.siteId}/addToCart`
        const method = 'POST'
        const body = {
            products: items.map((item) => this._constructEinsteinItem(item)),
            ...args,
        }

        return this.einsteinActivityFetch(endpoint, method, body)
    }

    /**
     * Get a list of recommenders that can be used in recommendation requests.
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getRecommenders() {
        const endpoint = `/personalization/recommenders/${this.siteId}`
        const method = 'GET'
        const body = null

        return this.einsteinFetch(endpoint, method, body)
    }

    /**
     * Get a set of recommendations
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getRecommendations(recommenderName, products, args) {
        const endpoint = `/personalization/recs/${this.siteId}/${recommenderName}`
        const method = 'POST'
        const body = {
            products: products?.map((product) => this._constructEinsteinProduct(product)),
            ...args,
        }

        return this.einsteinFetch(endpoint, method, body)
    }

    /**
     * Get a set of recommendations for a zone
     * https://developer.salesforce.com/docs/commerce/einstein-api/references#einstein-recommendations:Summary
     **/
    async getZoneRecommendations(zoneName, products, args) {
        const endpoint = `/personalization/${this.siteId}/zones/${zoneName}/recs`
        const method = 'POST'

        const body = {
            products: products?.map((product) => this._constructEinsteinProduct(product)),
            ...args,
        }

        return this.einsteinFetch(endpoint, method, body)
    }
}

const useEinstein = () => {
    const {effectiveDnt} = useDNT()
    const {site} = useMultiSite()
    const {
        app: {einsteinAPI: config},
    } = getConfig()
    const {host, einsteinId, siteId: configuredSiteId, isProduction} = config

    const siteId = resolveEinsteinSiteId(configuredSiteId, site?.id)

    const {getUsidWhenReady} = useUsid()
    const {getEncUserIdWhenReady} = useEncUserId()
    const {isRegistered} = useCustomerType()

    const einstein = useMemo(
        () =>
            new EinsteinAPI({
                host,
                einsteinId,
                siteId,
                isProduction,
                dnt: effectiveDnt,
            }),
        [host, einsteinId, siteId, isProduction, effectiveDnt],
    )
    // Recommendations fallback: before the per-site fix every site got the configured site's recs.
    // A site whose recommender is not set up in Einstein would now show an empty carousel, so retry there.
    const fallbackEinstein = useMemo(
        () => (siteId === configuredSiteId ? null : new EinsteinAPI({host, einsteinId, siteId: configuredSiteId, isProduction, dnt: effectiveDnt})),
        [host, einsteinId, siteId, configuredSiteId, isProduction, effectiveDnt],
    )
    const [isLoading, setIsLoading] = useState(false)
    const [recommendations, setRecommendations] = useState([])

    const getEventUserParameters = async () => {
        return {
            cookieId: await getUsidWhenReady(),
            userId: isRegistered ? await getEncUserIdWhenReady() : undefined,
        }
    }

    return {
        isLoading,

        recommendations,

        async sendViewProduct(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendViewProduct(...withUserParameters(args, 1, userParameters))
        },
        async sendViewSearch(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendViewSearch(...withUserParameters(args, 2, userParameters))
        },
        async sendClickSearch(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendClickSearch(...withUserParameters(args, 2, userParameters))
        },
        async sendViewCategory(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendViewCategory(...withUserParameters(args, 2, userParameters))
        },
        async sendClickCategory(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendClickCategory(...withUserParameters(args, 2, userParameters))
        },
        async sendViewPage(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendViewPage(...withUserParameters(args, 1, userParameters))
        },
        async sendBeginCheckout(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendBeginCheckout(...withUserParameters(args, 1, userParameters))
        },
        async sendCheckoutStep(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendCheckoutStep(...withUserParameters(args, 3, userParameters))
        },
        async sendViewReco(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendViewReco(...withUserParameters(args, 2, userParameters))
        },
        async sendClickReco(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendClickReco(...withUserParameters(args, 2, userParameters))
        },
        async sendAddToCart(...args) {
            const userParameters = await getEventUserParameters()
            return einstein.sendAddToCart(...withUserParameters(args, 1, userParameters))
        },
        async getRecommenders(...args) {
            return einstein.getRecommenders(...args)
        },
        async getRecommendations(recommenderName, products, ...args) {
            setIsLoading(true)
            try {
                const userParameters = await getEventUserParameters()
                const recoArgs = withUserParameters([recommenderName, products, ...args], 2, userParameters)
                let reco = await einstein.getRecommendations(...recoArgs)
                if (!reco?.recs?.length && fallbackEinstein) {
                    reco = await fallbackEinstein.getRecommendations(...recoArgs)
                }
                reco.recommenderName = recommenderName
                setRecommendations(reco)
            } catch (err) {
                logger.error('Error in getRecommendations', {
                    namespace: 'useEinstein.getRecommendations',
                    additionalProperties: {error: err},
                })
            } finally {
                setIsLoading(false)
            }
        },
        async getZoneRecommendations(zoneName, products, ...args) {
            setIsLoading(true)
            try {
                const userParameters = await getEventUserParameters()
                const reco = await einstein.getZoneRecommendations(...withUserParameters([zoneName, products, ...args], 2, userParameters))
                setRecommendations(reco)
            } catch (err) {
                logger.error('Error in getZoneRecommendations', {
                    namespace: 'useEinstein.getRecommendations',
                    additionalProperties: {error: err},
                })
            } finally {
                setIsLoading(false)
            }
        },
    }
}

export default useEinstein
